import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Reorder, useDragControls } from "framer-motion";
import { ArrowUp, ArrowDown, X, Plus, Lock, ListChecks, GripVertical } from "lucide-react";

const DRAG = { scale: 1.02 };
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { EnergyCheck } from "@/v2/EnergyCheck";
import { EnvelopeCard } from "@/v2/Pull";
import { YesterdayWrap, CarryCard } from "@/v2/DayCards";
import { FollowUpCard } from "@/v2/Plans";
import { LinkChip } from "@/v2/today3/MustDos";
import { FirmToggle } from "@/v2/today3/Firm";

const STYLES = [["gentle", "Gentle"], ["balanced", "Balanced"], ["direct", "Firm"]];
const move = (arr, i, d) => { const a = [...arr]; [a[i], a[i + d]] = [a[i + d], a[i]]; return a; };

function CheckStep({ t, first, onChecked, setT }) {
  const [busy, setBusy] = useState(false);
  const y = t.yesterday;
  const submit = async (body) => {
    setBusy(true);
    try { await api.post("/v2/today/check", body); await onChecked(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-3" data-testid="smd-step-1">
      {y && <p className="text-sm text-[#635E59] px-1" data-testid="smd-yesterday-recap">Yesterday: {y.status === "done" ? `you did "${y.text}".` : "a quieter day. Today's a fresh one."}</p>}
      {t.yesterday_open && <YesterdayWrap date={t.yesterday_date} onDone={onChecked} />}
      {t.envelope && <EnvelopeCard env={t.envelope} onOpened={(env) => setT((x) => ({ ...x, envelope: env }))} />}
      {t.plan_step_pending && <p className="text-sm text-stone-600 px-1" data-testid="smd-plan-pending">Your plan will be Must 1: <b>{t.plan_step_pending.text}</b></p>}
      <EnergyCheck name={first} hour={t.clock.hour} onSubmit={submit} busy={busy} />
    </div>
  );
}

function PlanRow({ m, i, last, onMove, onRemove }) {
  const controls = useDragControls();
  return (
    <Reorder.Item value={m} dragListener={false} dragControls={controls} whileDrag={DRAG} className="flex items-center gap-2 rounded-2xl border border-[#EFECE6] bg-white p-3 pl-1" data-testid={`smd-item-${i + 1}`}>
      <button onPointerDown={(e) => controls.start(e)} aria-label="Drag to reorder" data-testid={`smd-drag-${i + 1}`} style={{ touchAction: "none" }}
        className="h-11 w-6 shrink-0 grid place-items-center text-[#B8AFA3] cursor-grab active:cursor-grabbing"><GripVertical className="h-4 w-4" /></button>
      <span className="font-mono text-xs text-[#C86D51] w-4 shrink-0">{i + 1}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#2D2926] leading-snug">{m.text}</p>
        <div className="mt-1.5 flex flex-wrap items-center gap-2">{m.from_yesterday && <span className="text-[11px] font-semibold bg-[#FAF4E8] text-[#8A5A16] px-1.5 py-0.5 rounded" data-testid={`smd-from-yesterday-${i + 1}`}>From yesterday</span>}<LinkChip link={m.link} />{m.minutes != null && <span className="font-mono text-[11px] text-[#635E59]">{m.minutes}m</span>}</div>
      </div>
      <div className="flex flex-col">
        <button disabled={i === 0} onClick={() => onMove(i, -1)} aria-label="Move up" data-testid={`smd-up-${i + 1}`} className="h-11 w-11 grid place-items-center text-[#635E59] disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
        <button disabled={last} onClick={() => onMove(i, 1)} aria-label="Move down" data-testid={`smd-down-${i + 1}`} className="h-11 w-11 grid place-items-center text-[#635E59] disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
      </div>
      <button onClick={() => onRemove(i)} aria-label="Remove" data-testid={`smd-remove-${i + 1}`} className="h-11 w-11 grid place-items-center text-[#8C857B]"><X className="h-4 w-4" /></button>
    </Reorder.Item>
  );
}

function AddList({ title, rows, full, onAdd, testid }) {
  if (!rows.length) return null;
  return (
    <div data-testid={testid}>
      <p className="text-xs uppercase tracking-wider font-semibold text-[#8C857B] mb-1.5 px-1">{title}</p>
      <ul className="space-y-1.5">{rows.map((o) => (
        <li key={o.id} className="flex items-center gap-2 rounded-xl bg-[#F5F2EC] px-3 py-2">
          <span className="flex-1 min-w-0 text-sm text-[#2D2926] truncate">{o.text}</span>
          <button disabled={full} onClick={() => onAdd(o)} aria-label="Add" data-testid={`${testid}-add-${o.id.replace(/[^a-z0-9]/gi, "")}`} className="h-11 w-11 shrink-0 rounded-full bg-white grid place-items-center disabled:opacity-30"><Plus className="h-4 w-4" /></button>
        </li>))}</ul>
    </div>
  );
}

function PlanStep({ t, plan, setPlan, todos, onNext, onQuick, busy, reloadAll }) {
  const { items, options, cap } = plan;
  const full = items.length >= cap;
  const ids = new Set(items.map((x) => x.id));
  const set = (patch) => setPlan({ ...plan, ...patch });
  const add = (o) => set({ items: [...items, o], options: options.filter((x) => x.id !== o.id) });
  const remove = (i) => { const o = items[i]; set({ items: items.filter((_, j) => j !== i), options: o.source === "plan" && !plan.pool.has(o.id) ? options : [...options, o] }); };
  return (
    <div className="space-y-4" data-testid="smd-step-2">
      <p className="text-sm text-[#635E59]">{plan.shape === "rescue" ? "Gentle day: 1 or 2 small things is a real plan." : `Room for up to ${cap} today. Each one moves a goal, a plan or your mission. Reorder or drop anything.`}</p>
      {t.followup && <FollowUpCard plan={t.followup} onDone={reloadAll} today={null} />}
      {t.carry && <CarryCard step={t.carry} onDay={reloadAll} />}
      <Reorder.Group axis="y" values={items} onReorder={(next) => set({ items: next })} as="ul" className="space-y-2" data-testid="smd-list">
        {items.map((m, i) => <PlanRow key={m.id} m={m} i={i} last={i === items.length - 1} onMove={(a, d) => set({ items: move(items, a, d) })} onRemove={remove} />)}
      </Reorder.Group>
      {full && <p className="text-xs text-[#8C857B]" data-testid="smd-full">That's a full plan. Remove one to add another.</p>}
      <AddList title="More options" rows={options} full={full} onAdd={add} testid="smd-options" />
      <AddList title="From my to-dos" rows={todos.filter((p) => !ids.has(p.id) && !options.some((o) => o.id === p.id))} full={full} onAdd={add} testid="smd-todos" />
      <div className="grid grid-cols-2 gap-2 pt-1">
        <button disabled={busy || !items.length} onClick={onQuick} data-testid="smd-quick-lock" className="h-12 rounded-full border border-stone-300 text-sm font-semibold disabled:opacity-40">Just give me the plan</button>
        <button disabled={!items.length} onClick={onNext} data-testid="smd-next" className="btn-ink h-12 rounded-full text-sm font-semibold disabled:opacity-40">Next</button>
      </div>
    </div>
  );
}

function LockStep({ n, style, setStyle, commit, setCommit, by, setBy, onLock, onBack, busy }) {
  return (
    <div className="space-y-4" data-testid="smd-step-3">
      <label className="block text-sm font-semibold text-[#2D2926]">Your commitment
        <input value={commit} onChange={(e) => setCommit(e.target.value)} maxLength={160} placeholder={`Today I'll get ${n === 1 ? "this" : `these ${n}`} done.`} data-testid="smd-commitment" className="mt-1.5 w-full h-11 px-3 rounded-xl border border-[#EFECE6] text-base font-normal" />
      </label>
      <label className="block text-sm font-semibold text-[#2D2926]">Done by <span className="font-normal text-stone-400">· optional</span>
        <input type="time" value={by} onChange={(e) => setBy(e.target.value)} data-testid="smd-done-by" className="mt-1.5 w-40 h-11 px-3 rounded-xl border border-[#EFECE6] text-base font-normal block" />
      </label>
      <div>
        <p className="text-sm font-semibold text-[#2D2926] mb-1.5">How CAMRYN holds you to it</p>
        <div className="flex gap-1.5" data-testid="coach-style-selector">{STYLES.map(([k, l]) => <button key={k} onClick={() => setStyle(k)} data-testid={`smd-style-${k}`}
          className={`chip h-10 px-4 rounded-full text-sm border ${style === k ? "bg-stone-900 text-white border-stone-900" : "border-stone-200"}`}>{l}</button>)}</div>
        {style === "direct" && <p className="text-xs text-[#635E59] mt-2">Firm: plain words, real follow-up, no shame.</p>}
        <FirmToggle style={style} />
      </div>
      <button disabled={busy} onClick={onLock} data-testid="smd-lock" className="btn-ink w-full h-12 rounded-full font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"><Lock className="h-4 w-4" />{busy ? "Locking in…" : "Lock in my day"}</button>
      <button onClick={onBack} data-testid="smd-back" className="tap w-full text-sm text-[#635E59] underline">Back to my plan</button>
    </div>
  );
}

export function StartMyDay({ open, onClose, t, setT, first, reloadAll, onLocked }) {
  const [step, setStep] = useState(1);
  const [plan, setPlan] = useState(null);
  const [todos, setTodos] = useState([]);
  const [style, setStyle] = useState(t.journey.coaching_style);
  const [commit, setCommit] = useState("");
  const [by, setBy] = useState("");
  const [busy, setBusy] = useState(false);

  const loadPlan = async () => {
    const [{ data }, pl] = await Promise.all([api.get("/v2/today/plan"), api.get("/v2/plans")]);
    setPlan({ ...data, pool: new Set([...data.items, ...data.options].map((x) => x.id)) });
    setTodos(pl.data.open.filter((p) => pl.data.today?.plan_id !== p.id).map((p) => ({ id: `plan:${p.id}`, source: "plan", text: p.text, link: { label: "Your plan" }, minutes: 15 })));
    setStep(2);
  };
  useEffect(() => { if (open) { if (t.day) loadPlan().catch((e) => toast.error(errMsg(e))); else setStep(1); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const lock = async (quick) => {
    setBusy(true);
    try {
      if (!quick && style !== t.journey.coaching_style) await api.patch("/v2/journey", { coaching_style: style });
      await api.post("/v2/today/plan/lock", { ids: plan.items.map((x) => x.id), commitment: quick ? null : commit || null, done_by: quick ? null : by || null });
      toast.success("Locked in. Let's go.");
      await onLocked();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const titles = { 1: "Start my day", 2: "Your plan", 3: "Lock it in" };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-lg overflow-y-auto overflow-x-hidden rounded-2xl p-4 sm:p-6 [&>*]:min-w-0" data-testid="start-my-day-sheet">
        <DialogHeader>
          <span className="text-[11px] uppercase tracking-wider font-semibold text-[#C86D51] flex items-center gap-1.5"><ListChecks className="h-3.5 w-3.5" />Step {step} of 3</span>
          <DialogTitle className="font-display text-2xl">{titles[step]}</DialogTitle>
          <DialogDescription className="sr-only">Check in, choose today's must-dos and lock them in.</DialogDescription>
        </DialogHeader>
        {step === 1 && <CheckStep t={t} first={first} setT={setT} onChecked={async () => { await reloadAll(); await loadPlan(); }} />}
        {step === 2 && plan && <PlanStep t={t} plan={plan} setPlan={setPlan} todos={todos} busy={busy} onNext={() => setStep(3)} onQuick={() => lock(true)}
          reloadAll={async () => { await reloadAll(); await api.get("/v2/today/plan").then(({ data }) => setPlan((p) => ({ ...p, options: data.options, items: p.items.map((x) => data.items.concat(data.options).find((y) => y.id === x.id) || x) }))); }} />}
        {step === 3 && <LockStep n={plan.items.length} style={style} setStyle={setStyle} commit={commit} setCommit={setCommit} by={by} setBy={setBy} onLock={() => lock(false)} onBack={() => setStep(2)} busy={busy} />}
      </DialogContent>
    </Dialog>
  );
}
